import type { EstimationItemResponse } from '@/api/schema/data-contracts';
import { getDescendants } from '@/hooks/useEstimationExport/helperFunctions';

/**
 * Gets all estimation item IDs for a phase and all its descendants (children, grandchildren, etc.)
 * @param phaseId The ID of the phase (top-level estimation item)
 * @param allEstimationItems All estimation items in the estimation
 * @returns Array of estimation item IDs including the phase itself and all its descendants
 */
export const getPhaseAndDescendantIds = (
  phaseId: string,
  allEstimationItems: EstimationItemResponse[],
): string[] => {
  const phase = allEstimationItems.find((item) => item.id === phaseId);
  if (!phase) {
    return [];
  }

  const descendants = getDescendants(allEstimationItems, phaseId);
  return [phaseId, ...descendants.map((item) => item.id)];
};

/**
 * Checks if an estimation item should be hidden based on hidden phases
 * @param estimationItemId The ID of the estimation item to check
 * @param hiddenPhases Array of phase IDs that should be hidden
 * @param allEstimationItems All estimation items in the estimation
 * @returns true if the item should be hidden, false otherwise
 */
export const shouldHideEstimationItem = (
  estimationItemId: string,
  hiddenPhases: string[],
  allEstimationItems: EstimationItemResponse[],
): boolean => {
  if (hiddenPhases.length === 0) {
    return false;
  }

  // Check if this item is a descendant of any hidden phase
  for (const hiddenPhaseId of hiddenPhases) {
    const phaseAndDescendantIds = getPhaseAndDescendantIds(hiddenPhaseId, allEstimationItems);
    if (phaseAndDescendantIds.includes(estimationItemId)) {
      return true;
    }
  }

  return false;
};
